import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';

interface FrameworkRow {
  readonly frameworkId: string;
  readonly frameworkCode: string;
  readonly frameworkName: string;
  readonly frameworkType: string;
  readonly status: string;
  readonly totalControls: number;
  readonly compliantControls: number;
  readonly complianceScore: string;
}

const TYPES = ['', 'STANDARD', 'REGULATION', 'CERTIFICATION', 'INTERNAL', 'CONTRACTUAL'];
const STATUSES = ['', 'DRAFT', 'ACTIVE', 'UNDER_REVIEW', 'RETIRED'];

/**
 * M30 — Compliance Dashboard (§16 / §31). Portfolio view of compliance
 * frameworks (ISO 27001, SOC 2, GDPR, DPDP, PCI-DSS …) with their control
 * counts and rolled-up compliance score, so GRC can track posture per
 * framework. Compliance evidence is retained for every assessed control (BR-04).
 */
export function M30ComplianceDashboardPage(): ReactNode {
  const { decimal } = useBusinessNumber();
  const { t: tr } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<FrameworkRow[]>([]);
  const [frameworkType, setFrameworkType] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (frameworkType) params.set('frameworkType', frameworkType);
    if (status) params.set('status', status);
    const qs = params.toString();
    apiRequest<{ data: FrameworkRow[] }>(`/v1/compliance/frameworks${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, frameworkType, status]);

  return (
    <main aria-label="compliance-dashboard">
      <h1>{tr('m30.compliance.title')}</h1>
      <div className="filters">
        <label>
          {tr('m30.compliance.filterType')}
          <select aria-label="filter-framework-type" value={frameworkType} onChange={(e) => setFrameworkType(e.target.value)}>
            {TYPES.map((t) => <option key={t || 'all'} value={t}>{t || tr('m30.compliance.all')}</option>)}
          </select>
        </label>
        <label>
          {tr('m30.compliance.filterStatus')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || tr('m30.compliance.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{tr('m30.compliance.loading')}</p> : (
        <>
          <p aria-label="framework-count">{tr('m30.compliance.count', { count: rows.length })}</p>
          <table aria-label="framework-table">
            <thead><tr><th>{tr('m30.compliance.thCode')}</th><th>{tr('m30.compliance.thName')}</th><th>{tr('m30.compliance.thType')}</th><th>{tr('m30.compliance.thStatus')}</th><th>{tr('m30.compliance.thControls')}</th><th>{tr('m30.compliance.thCompliant')}</th><th>{tr('m30.compliance.thScore')}</th></tr></thead>
            <tbody>
              {rows.map((f) => (
                <tr key={f.frameworkId}>
                  <td>{f.frameworkCode}</td><td>{f.frameworkName}</td><td>{f.frameworkType}</td>
                  <td>{f.status}</td><td>{f.totalControls}</td><td>{f.compliantControls}</td>
                  <td>{decimal(f.complianceScore)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </main>
  );
}
